/**
 * Household feature — Ducks pattern (actions, reducer, types).
 */

import { User, Household } from '@/infra/types/domain';

// ─── State Shape ───────────────────────────────────────────

export interface HouseholdState {
  household: Household | null;
  users: User[];
  currentUserId: string | null;
  loading: boolean;
  error: string | null;
}

export const initialState: HouseholdState = {
  household: null,
  users: [],
  currentUserId: null,
  loading: false,
  error: null,
};

// ─── Action Types ──────────────────────────────────────────

const SET_HOUSEHOLD = 'household/setHousehold';
const SET_USERS = 'household/setUsers';
const SET_CURRENT_USER = 'household/setCurrentUser';
const SET_LOADING = 'household/setHouseholdLoading';
const SET_ERROR = 'household/setHouseholdError';

// Trigger actions
export const FETCH_HOUSEHOLD = 'household/fetchHousehold';

// ─── Action Creators ───────────────────────────────────────

export function setHousehold(household: Household) {
  return { type: SET_HOUSEHOLD, payload: household } as const;
}

export function setUsers(users: User[]) {
  return { type: SET_USERS, payload: users } as const;
}

export function setCurrentUser(userId: string) {
  return { type: SET_CURRENT_USER, payload: userId } as const;
}

export function setHouseholdLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setHouseholdError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function fetchHousehold() {
  return { type: FETCH_HOUSEHOLD } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type HouseholdAction =
  | ReturnType<typeof setHousehold>
  | ReturnType<typeof setUsers>
  | ReturnType<typeof setCurrentUser>
  | ReturnType<typeof setHouseholdLoading>
  | ReturnType<typeof setHouseholdError>;

// ─── Reducer ───────────────────────────────────────────────

export function householdReducer(
  state: HouseholdState = initialState,
  action: HouseholdAction | { type: string; payload?: unknown }
): HouseholdState {
  switch (action.type) {
    case SET_HOUSEHOLD: {
      const household = (action as ReturnType<typeof setHousehold>).payload;
      return {
        ...state,
        household,
      };
    }

    case SET_USERS: {
      const users = (action as ReturnType<typeof setUsers>).payload;
      return { ...state, users };
    }

    case SET_CURRENT_USER: {
      const userId = (action as ReturnType<typeof setCurrentUser>).payload;
      return { ...state, currentUserId: userId };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setHouseholdLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setHouseholdError>).payload;
      return { ...state, error };
    }

    default:
      return state;
  }
}
